import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from "react-router-dom";

import Paginator from '../../components/ui/Paginator';
import Message from '../../components/ui/Message';
import { Waiter } from '../../models/interfaces';
import { WaitersContext } from '../../context/waiter/WaiterContext';


const PAGE_SIZE = 10;

export const WaitersPage = () => {
    const [buscar, setBuscar] = useState('');
    const [pageActual, setPageActual] = useState<number>(1);
    const [isLoading, setIsLoading] = useState(false);

    const [error, setError] = useState('');
    const [confirm, setConfirm] = useState('');
    const [waiterId, setWaiterId] = useState<number | null>(null);

    const { waiters, loadWaiters, deleteWaiter } = useContext(WaitersContext);
    const navigate = useNavigate();

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                await loadWaiters(0);
            } catch (e) {
                setError('No se pudieron cargar los meseros, intente de nuevo.');
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [])

    useEffect(() => {
        setPageActual(1);
    }, [buscar])

    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        if (!term) return waiters;
        return waiters.filter((waiter) => String(waiter.name ?? '').toLowerCase().includes(term));
    }, [waiters, buscar]);

    const totalRecords = filteredData.length;
    const totalPage = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
    const pageItems = filteredData.slice((pageActual - 1) * PAGE_SIZE, pageActual * PAGE_SIZE);

    // Si al eliminar se reduce el número de páginas, se ajusta la página actual
    useEffect(() => {
        if (pageActual > totalPage) setPageActual(totalPage);
    }, [totalPage])

    const handleNew = () => {
        navigate("/waiter");
    }

    const handleEdit = (waiter: Waiter) => {
        navigate("/waiter", { state: { waiter } });
    }

    const handleDelete = (waiter: Waiter) => {
        setWaiterId(waiter.id);
        setConfirm(`¿Está seguro que desea eliminar el mesero "${waiter.name}"?`);
    }

    const processDelete = async () => {
        setConfirm('');
        if (waiterId === null) return;
        const resp = await deleteWaiter(waiterId);
        if (resp !== "success") {
            setError(resp);
        }
        setWaiterId(null);
    }

    const startIndex = totalRecords === 0 ? 0 : (pageActual - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(pageActual * PAGE_SIZE, totalRecords);

    const removeLocalError = () => setError('');
    const removeConfirmMessage = () => { setConfirm(''); setWaiterId(null); };

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Meseros</h3>
                    <p className="text-muted mb-0">Administre los meseros que atienden las mesas de su comercio.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-primary" onClick={handleNew}>
                        <i className="bi bi-plus-lg me-1" /> Agregar mesero
                    </button>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">

                    <div className="row g-2 mb-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input
                                    className="form-control"
                                    id="search"
                                    type="text"
                                    placeholder="Buscar por nombre..."
                                    value={buscar}
                                    onChange={(e) => setBuscar(e.target.value)}
                                />
                                {buscar && (
                                    <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table id='waiterTable' className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Nombre</th>
                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={2} className='text-center py-4'>
                                        <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                    </td></tr>
                                ) : pageItems.length > 0 ? (
                                    pageItems.map(waiter => (
                                        <tr key={waiter.id}>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div style={styles.avatar}>
                                                        {String(waiter.name ?? '?').trim().charAt(0).toUpperCase()}
                                                    </div>
                                                    <span className="fw-medium">{waiter.name}</span>
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center', width: 110 }}>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-primary"
                                                    title="Editar mesero"
                                                    onClick={() => handleEdit(waiter)}
                                                >
                                                    <i className="bi bi-pencil" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger ms-1"
                                                    title="Eliminar mesero"
                                                    onClick={() => handleDelete(waiter)}
                                                >
                                                    <i className="bi bi-trash" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan={2} className='text-center py-4 text-muted'>
                                        {buscar ? 'No se encontraron meseros para la búsqueda.' : 'No existen meseros.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                        <span className="text-muted small">
                            {totalRecords > 0 ? `Mostrando ${startIndex}-${endIndex} de ${totalRecords} meseros` : ''}
                        </span>
                        {totalPage > 1 ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {error.length > 0 && (
                <Message
                    type="error"
                    message={error}
                    closeDialog={removeLocalError}
                    handleOk={removeLocalError}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}

            {confirm.length > 0 && (
                <Message
                    type="success"
                    title="Atención"
                    message={confirm}
                    closeDialog={removeConfirmMessage}
                    showCancel={true}
                    handleCancel={removeConfirmMessage}
                    handleOk={processDelete}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
        </div>
    )
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
    avatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#e7f1ff',
        color: '#0d6efd',
        fontWeight: 600,
        flexShrink: 0,
    },
};
